import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import ReceiptCard from './ReceiptCard';
import GivelifyBottomSheet from '../../common/Modal/GivelifyBottomSheet';
import { Donee, ReceiptResponseData } from '../../../../../api';
import { Text } from '../../common/Text/Text';
import { isAppleDevice } from '../../common/utils';
import { usePushEvents } from '../../../../../hooks/PushEvents';
import {
    appDownloadEvent,
    clickEvent,
    successEvent,
    viewEvent,
} from '../../../../../hooks/PushEvents/clevertapEvents';
import { useDonorProfile } from '../../../../../api';
import { useDeepLink } from '../../../../../hooks/useDeepLink';
import DeepLinkCopiedModal from './DeepLinkCopiedModal';
import { Button } from '../../../../ui';

const postEventPayload = {
    name: 'Thank you screen receipt modal',
    location: 'receiptbottomsheet',
    action: 'completed',
    voucher: 'DDL voucher',
};

const eventPayload = {
    location: 'receiptbottomsheet',
    action: 'clicked',
};

const deepLinkTitle = (
    <>
        We’ve <span className="deep-link-copied-text">copied</span> a link
        <br />
        to your full receipt
    </>
);

const disableScroll = () => {
    // Scroll to the bottom of the page
    window.scrollTo(0, document.body.scrollHeight);
    // Disable scrolling
    // const scrollY = window.scrollY;
    // document.body.style.position = 'fixed';
    // document.body.style.top = `-${scrollY}px`;
};

const enableScroll = () => {
    // Scroll to the top of the page
    window.scrollTo(0, 0);
    // Enable scrolling
    // const scrollY = document.body.style.top;
    // document.body.style.position = '';
    // document.body.style.top = '';
    // window.scrollTo(0, parseInt(scrollY || '0') * -1);
};

const appStoreLink =
    'https://apps.apple.com/app/apple-store/id725815127?pt=8388804&ct=oga_appdownload_receiptbottomsheet&mt=8';
const playStoreLink =
    'https://play.google.com/store/apps/details?id=com.pushcontrolz.givelify&referrer=utm_source%3Doga%26utm_medium%3Dweb%26utm_campaign%3Doga_appdownload_receiptbottomsheet';

const handleDownloadButton = () => {
    // copyDoneeIdToClipboard();
    window.location.href = isAppleDevice() ? appStoreLink : playStoreLink;
};

interface ReceiptModalProps {
    donee: Donee;
    isReceiptBlur: boolean;
    receiptData?: ReceiptResponseData;
    isReceiptLoading: boolean;
}

const StyledDiv = styled.div`
    .receipt-modal-heading {
        font-weight: 800;
        text-align: center;
        max-width: 240px;
        margin-left: auto;
        margin-right: auto;
    }
    .receipt-card {
        margin-top: 16px;
        margin-bottom: 24px;
        filter: drop-shadow(0px 1px 2px #ddd);
    }
`;

const showThis = window.innerWidth < 501;
const ReceiptModal: React.FC<ReceiptModalProps> = ({
    donee,
    isReceiptBlur,
    receiptData,
    isReceiptLoading,
}) => {
    const [url, setUrl] = useState<string | undefined>(undefined);
    const { donorProfile } = useDonorProfile();
    const { pushEvent } = usePushEvents();
    const [show, setShow] = useState<boolean>(showThis);
    const { deepLinkState, getDeepLink, showCopiedModal, setShowCopiedModal } =
        useDeepLink(undefined, eventPayload, handleDownloadButton);

    useEffect(() => {
        if (show) {
            disableScroll();
        } else {
            enableScroll();
        }
        return () => enableScroll();
    }, [show]);

    useEffect(() => {
        if (show) {
            pushEvent(appDownloadEvent, {
                location: 'receiptbottomsheet',
                action: 'shown',
            });
        }
    }, [pushEvent, show]);

    const handleDownloadClicked = useCallback(() => {
        pushEvent(appDownloadEvent, eventPayload);
        if (receiptData?.gift) {
            getDeepLink(donee, donorProfile, '/dl/receipt', {
                key: 'receipt',
                data: receiptData.gift,
            }).then((s) => {
                s && setUrl(s);
            });
        } else {
            handleDownloadButton();
        }
    }, [pushEvent, receiptData, getDeepLink, donee, donorProfile]);

    const handleClose = useCallback(() => {
        enableScroll();
        setShow(false);
        pushEvent(appDownloadEvent, {
            location: 'receiptbottomsheet',
            action: 'dismissed',
        });
    }, [pushEvent]);

    if (!show) return null;

    return (
        <>
            <GivelifyBottomSheet
                onClosedCallback={handleClose}
                initialOpenValue={window.innerWidth < 501}
                contentPadding="12px 24px 24px 24px"
            >
                <StyledDiv>
                    <Text
                        variant="large"
                        className="receipt-modal-heading"
                    >
                        Get the free Givelify App to view your full receipt
                    </Text>
                    <ReceiptCard
                        organizationName={donee.organization.name}
                        isReceiptBlur={isReceiptBlur}
                        gift={receiptData?.gift}
                        envelopes={receiptData?.envelopes}
                        isLoading={isReceiptLoading}
                        className="receipt-card"
                    />
                    <Button
                        color="primary"
                        loading={isReceiptLoading || deepLinkState.loading}
                        onClick={handleDownloadClicked}
                        className="w-full"
                    >
                        Get the Givelify App
                    </Button>
                </StyledDiv>
            </GivelifyBottomSheet>
            <DeepLinkCopiedModal
                url={url}
                open={showCopiedModal}
                title={deepLinkTitle}
                viewEvent={viewEvent}
                clickEvent={clickEvent}
                successEvent={successEvent}
                clickEventPayload={postEventPayload}
                onClick={handleDownloadButton}
                onClose={() => setShowCopiedModal(false)}
            />
        </>
    );
};

export default ReceiptModal;
